// Pure helpers for the flat Follow Up Paths UI (§25). Everything here is
// deterministic and side-effect free so the Playwright no-deps spec can
// exercise the exact payload the publish RPC receives.
//
// Mirrors the deployed contract in
// reachchms-functions/supabase/schemas/functions/public_follow_up_publish_path.sql
//   steps[]: {stepID?, actionType, label?, instructions?, waitDays,
//             expiresAfterDays?, config?}
//   config per action:
//     SEND_EMAIL → {emailTemplateID}
//     SEND_SMS   → {message}
//     OUTREACH   → {channel: PHONE_CALL|IN_PERSON|TEXT|EMAIL|OTHER, assignment}
//     TASK       → {assignment}
//   assignment: {mode:'SPECIFIC', personID} | {mode:'PRIMARY_CONNECTOR'}
//               | {mode:'POOL', poolMemberIDs:[...]}
//   waitDays int >= 0; expiresAfterDays int > 0 or null = No time limit
//   (human actions only).
// Stages/requirements/kind are retired — the RPC rejects them, so the
// client never builds them.

import {
    getStepActionLabel,
    stepActionIsHuman,
// Relative (not '@/'-aliased) so Playwright specs can import this module
// directly through Node resolution — same convention as formFilePolicy.js.
} from './constants/followUp';

const VALID_ACTION_TYPES = new Set(['SEND_EMAIL', 'SEND_SMS', 'OUTREACH', 'TASK']);
const VALID_CHANNELS = new Set(['PHONE_CALL', 'IN_PERSON', 'TEXT', 'EMAIL', 'OTHER']);
const VALID_ASSIGNMENT_MODES = new Set(['SPECIFIC', 'PRIMARY_CONNECTOR', 'POOL']);

// Human work defaults to a three-day deadline (§25.2); null on
// expiresAfterDays persists "No time limit" — never confused with 0.
export const DEFAULT_STEP_EXPIRY_DAYS = 3;

// Local-only ids for steps created in the editor that have not been
// published yet — the RPC treats an omitted id as "new row".
export const newLocalID = () => `local-${Math.random().toString(36).slice(2, 10)}`;
export const isLocalID = (id) => typeof id === 'string' && id.startsWith('local-');

/**
 * Blank step for the editor. waitDays defaults to zero (explicit "after
 * enrollment"/"after previous step"); expiresAfterDays defaults to the
 * three-day human deadline — irrelevant for automatic actions.
 */
export const emptyStep = (actionType = 'OUTREACH') => ({
    stepID: newLocalID(),
    actionType,
    label: '',
    instructions: '',
    waitDays: 0,
    expiresAfterDays: stepActionIsHuman(actionType) ? DEFAULT_STEP_EXPIRY_DAYS : null,
    config: actionType === 'OUTREACH'
        ? { channel: 'PHONE_CALL', assignment: { mode: 'PRIMARY_CONNECTOR' } }
        : stepActionIsHuman(actionType)
            ? { assignment: { mode: 'PRIMARY_CONNECTOR' } }
            : {},
});

/**
 * Convert a follow_up_get_path definition into editor state — a defensive
 * deep copy so local edits never mutate the react-query cache object.
 * Pool member names ride along on `poolMembers` for the picker; the ids
 * themselves live in config.assignment.poolMemberIDs.
 */
export const definitionToDraft = (path) => ({
    pathID: path?.pathID ?? null,
    name: path?.name ?? '',
    description: path?.description ?? '',
    isActive: path?.isActive !== false,
    isArchived: path?.isArchived === true,
    revision: path?.revision ?? null,
    activeEnrollmentCount: path?.activeEnrollmentCount ?? 0,
    steps: (path?.steps || []).map((step) => ({
        stepID: step.stepID ?? newLocalID(),
        actionType: step.actionType ?? null,
        label: step.label ?? '',
        instructions: step.instructions ?? '',
        waitDays: step.waitDays ?? 0,
        expiresAfterDays: step.expiresAfterDays ?? null,
        config: step.config && typeof step.config === 'object'
            ? JSON.parse(JSON.stringify(step.config))
            : {},
        poolMembers: Array.isArray(step.poolMembers) ? [...step.poolMembers] : [],
    })),
});

const parseDayInt = (value) => {
    if (value === null || value === undefined || `${value}`.trim() === '') return null;
    const n = Number(value);
    return Number.isInteger(n) ? n : NaN;
};

/**
 * Validate a draft the way the RPC will (client-side, fail fast — the server
 * remains authoritative). Returns an array of human-readable error strings.
 */
export const validatePathDraft = (draft) => {
    const errors = [];
    if (!`${draft?.name ?? ''}`.trim()) errors.push('The path needs a name.');

    const steps = draft?.steps || [];
    if (steps.length === 0) errors.push('Add at least one step.');

    steps.forEach((step, sIdx) => {
        const stepLabel = `Step ${sIdx + 1}`;
        const actionType = step?.actionType;
        if (!VALID_ACTION_TYPES.has(actionType)) {
            errors.push(`${stepLabel} needs an action.`);
        }

        const wait = parseDayInt(step?.waitDays ?? 0);
        if (wait === null || Number.isNaN(wait) || wait < 0) {
            errors.push(`${stepLabel} wait must be zero or more whole days.`);
        }

        const human = stepActionIsHuman(actionType);
        if (human) {
            const expires = step?.expiresAfterDays;
            if (expires !== null && expires !== undefined && `${expires}` !== '') {
                const e = parseDayInt(expires);
                if (Number.isNaN(e) || e === null || e <= 0) {
                    errors.push(`${stepLabel} time limit must be a positive whole number of days, or No time limit.`);
                }
            }

            const assignment = step?.config?.assignment;
            const mode = assignment?.mode;
            if (!VALID_ASSIGNMENT_MODES.has(mode)) {
                errors.push(`${stepLabel} needs an assignment.`);
            } else if (mode === 'SPECIFIC' && !assignment?.personID) {
                errors.push(`${stepLabel} needs an assignee.`);
            } else if (mode === 'POOL'
                && !(Array.isArray(assignment?.poolMemberIDs) && assignment.poolMemberIDs.length > 0)) {
                errors.push(`${stepLabel} needs at least one pool member.`);
            }
        } else {
            // Automatic actions must not carry a deadline or assignment —
            // the sanitizer drops them, but a configured value is a bug.
            if (actionType === 'SEND_EMAIL' && !`${step?.config?.emailTemplateID ?? ''}`.trim()) {
                errors.push(`${stepLabel} needs an email template.`);
            }
            if (actionType === 'SEND_SMS' && !`${step?.config?.message ?? ''}`.trim()) {
                errors.push(`${stepLabel} needs message text.`);
            }
        }

        if (actionType === 'OUTREACH' && !VALID_CHANNELS.has(step?.config?.channel)) {
            errors.push(`${stepLabel} needs a contact channel.`);
        }
    });

    return errors;
};

/** Strip helper/empty keys from one step's config before publish. */
const sanitizeStepConfig = (step) => {
    const config = step?.config && typeof step.config === 'object' ? step.config : {};
    switch (step?.actionType) {
        case 'SEND_EMAIL': {
            const emailTemplateID = `${config.emailTemplateID ?? ''}`.trim();
            return emailTemplateID ? { emailTemplateID } : {};
        }
        case 'SEND_SMS': {
            const message = `${config.message ?? ''}`.trim();
            return message ? { message } : {};
        }
        case 'OUTREACH':
        case 'TASK': {
            const clean = {};
            if (step.actionType === 'OUTREACH' && VALID_CHANNELS.has(config.channel)) {
                clean.channel = config.channel;
            }
            const assignment = config.assignment;
            if (assignment && typeof assignment === 'object' && VALID_ASSIGNMENT_MODES.has(assignment.mode)) {
                const a = { mode: assignment.mode };
                if (assignment.mode === 'SPECIFIC' && assignment.personID) {
                    a.personID = assignment.personID;
                }
                if (assignment.mode === 'POOL' && Array.isArray(assignment.poolMemberIDs)) {
                    a.poolMemberIDs = assignment.poolMemberIDs.filter(Boolean);
                }
                clean.assignment = a;
            }
            return clean;
        }
        default:
            return {};
    }
};

/**
 * Build the exact p_payload object for follow_up_publish_path from an editor
 * draft (definitionToDraft shape). Local-only ids are omitted so the RPC
 * creates new rows; published steps absent from the payload are retired
 * server-side. Returns { errors, payload } — payload is null when errors
 * exist.
 */
export const buildPublishPayload = (draft) => {
    const errors = validatePathDraft(draft);
    if (errors.length) return { errors, payload: null };

    const steps = (draft.steps || []).map((step, sIdx) => {
        const human = stepActionIsHuman(step.actionType);
        const wait = parseDayInt(step.waitDays ?? 0);
        const built = {
            ...(isLocalID(step.stepID) ? {} : { stepID: step.stepID }),
            actionType: step.actionType,
            sortOrder: (sIdx + 1) * 10,
            waitDays: wait ?? 0,
            config: sanitizeStepConfig(step),
        };
        if (`${step.label ?? ''}`.trim()) built.label = step.label.trim();
        if (`${step.instructions ?? ''}`.trim()) built.instructions = step.instructions.trim();
        // null = No time limit (human only); zero is a wait value, never a
        // deadline — keep them distinct per contract.
        if (human && step.expiresAfterDays != null && `${step.expiresAfterDays}` !== '') {
            built.expiresAfterDays = parseDayInt(step.expiresAfterDays);
        }
        return built;
    });

    const payload = {
        name: `${draft.name}`.trim(),
        ...(draft.description?.trim() ? { description: draft.description.trim() } : {}),
        isActive: draft.isActive !== false,
        isArchived: draft.isArchived === true,
        steps,
    };
    if (draft.pathID) {
        payload.pathID = draft.pathID;
        payload.expectedRevision = draft.revision;
    }
    return { errors: [], payload };
};

/** Generated step title — the action label unless a custom label is set. */
export const stepTitle = (step, fallback = 'New step') =>
    `${step?.label ?? ''}`.trim() || getStepActionLabel(step) || fallback;

const dayText = (days, singular, plural = `${singular}s`) =>
    `${days} ${days === 1 ? singular : plural}`;

/**
 * Human-readable summary for a definition step — used on editor rows and
 * the template detail preview. `names` optionally resolves ids:
 * { templateName, assigneeName, poolNames }.
 */
export const describeStep = (step, index = 0, names = {}) => {
    const parts = [];

    const wait = parseDayInt(step?.waitDays ?? 0) ?? 0;
    parts.push(wait > 0
        ? `Wait ${dayText(wait, 'day')} after ${index === 0 ? 'enrollment' : 'previous step'}`
        : `After ${index === 0 ? 'enrollment' : 'previous step'}`);

    switch (step?.actionType) {
        case 'SEND_EMAIL':
            parts.push(names.templateName
                ? `Template: ${names.templateName}`
                : 'Uses an email template');
            break;
        case 'SEND_SMS': {
            const message = `${step?.config?.message ?? ''}`.trim();
            parts.push(message ? `"${message.length > 60 ? `${message.slice(0, 60)}…` : message}"` : 'Text message');
            break;
        }
        case 'OUTREACH':
        case 'TASK': {
            const assignment = step?.config?.assignment || {};
            if (assignment.mode === 'SPECIFIC') {
                parts.push(`Assign to ${names.assigneeName || 'a specific person'}`);
            } else if (assignment.mode === 'POOL') {
                const count = names.poolNames?.length ?? assignment.poolMemberIDs?.length ?? 0;
                parts.push(`Assign to pool${count ? ` (${count} ${count === 1 ? 'person' : 'people'})` : ''}`);
            } else {
                parts.push('Assign to primary connector');
            }
            const expires = step?.expiresAfterDays;
            parts.push(expires != null && `${expires}` !== ''
                ? `Allow ${dayText(parseDayInt(expires) ?? 0, 'day')} to complete`
                : 'No time limit');
            break;
        }
        default:
            break;
    }

    if (`${step?.instructions ?? ''}`.trim()) {
        parts.push(step.instructions.trim());
    }
    return parts.join(' · ');
};

/**
 * §25.8 progress math — the single shared denominator (effective =
 * non-RETIRED steps) drives BOTH percentages so a partial Path never reads
 * as "100% successful":
 *   pathProgress    = progressed / effective  (SUCCEEDED+EXPIRED+SKIPPED)
 *   stepsSuccessful = successful / effective  (SUCCEEDED only)
 * Cancelled work stays in the muted remainder — an early stop never
 * manufactures 100%. An unfinished Path caps at 99% so rounding can't
 * claim completion.
 */
export const computePathProgress = (progress) => {
    const effective = Math.max(0, Number(progress?.effective) || 0);
    const progressed = Math.min(Math.max(0, Number(progress?.progressed) || 0), effective);
    const successful = Math.min(Math.max(0, Number(progress?.successful) || 0), progressed);
    const expired = Math.max(0, Number(progress?.expired) || 0);
    const skipped = Math.max(0, Number(progress?.skipped) || 0);
    const missed = Math.max(0, progressed - successful);

    const pct = (n) => (effective > 0 ? Math.round((n / effective) * 100) : 0);
    const cap = (n, v) => (effective > 0 && n < effective ? Math.min(99, v) : v);

    return {
        effective,
        progressed,
        successful,
        expired,
        skipped,
        missed,
        pathProgressPct: cap(progressed, pct(progressed)),
        stepsSuccessfulPct: cap(successful, pct(successful)),
        successfulWidth: effective > 0 ? (successful / effective) * 100 : 0,
        missedWidth: effective > 0 ? (missed / effective) * 100 : 0,
    };
};
